import React, { useEffect, useState } from 'react';
import { Modal, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getPointsRecalculationApi, recalculatePointsApi } from '@app/api/customer.api';
import { PointsRecalculationRow } from '@app/interfaces/pointTran';
import { formatDateTime, formatMoney } from '@app/utils/utils';

interface Props {
  open: boolean;
  customerId: number;
  onClose: () => void;
  onDone: () => void;
}

const columns: ColumnsType<PointsRecalculationRow> = [
  { title: 'Mã đơn', dataIndex: 'order_code', key: 'order_code' },
  { title: 'Ngày', dataIndex: 'trans_date', key: 'trans_date', render: (value: string) => formatDateTime(value, 'date') },
  { title: 'Giá trị', dataIndex: 'total_amount', key: 'total_amount', align: 'right', render: (value: number) => formatMoney(value) },
  {
    title: 'Điểm bù',
    dataIndex: 'points',
    key: 'points',
    align: 'right',
    render: (value: number, row) => (
      <>
        {value}
        {row.expired && <Tag className="tw-ml-2">Đã hết hạn</Tag>}
      </>
    ),
  },
];

/** Xem trước các đơn thiếu điểm rồi mới bù, để nhân viên biết chính xác số điểm sẽ cộng cho khách. */
const RecalculatePointsModal: React.FC<Props> = ({ open, customerId, onClose, onDone }) => {
  const [rows, setRows] = useState<PointsRecalculationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let ignore = false;
    setLoading(true);
    getPointsRecalculationApi(customerId)
      .then((result) => {
        if (!ignore) setRows(result);
      })
      .catch((error: Error) => {
        if (!ignore) message.error(error.message || 'Không kiểm tra được đơn thiếu điểm');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [open, customerId]);

  const usablePoints = rows.filter((row) => !row.expired).reduce((sum, row) => sum + row.points, 0);

  const handleApply = async () => {
    setSaving(true);
    try {
      await recalculatePointsApi(customerId);
      message.success(`Đã bù điểm cho ${rows.length} đơn`);
      onDone();
    } catch (error: any) {
      message.error(error.message || 'Không bù được điểm');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Tính lại điểm tích luỹ"
      width={640}
      okText="Bù điểm"
      cancelText="Huỷ"
      okButtonProps={{ disabled: rows.length === 0 }}
      confirmLoading={saving}
      onOk={handleApply}
      onCancel={onClose}
    >
      <p className="tw-text-sm tw-text-on-surface-variant">
        {rows.length > 0
          ? `Có ${rows.length} đơn đã hoàn tất, đủ mốc mà chưa có điểm. Khách sẽ được cộng ${usablePoints} điểm dùng được; đơn năm trước chỉ ghi lịch sử vì đã hết hạn. Đơn đã có điểm giữ nguyên.`
          : 'Không có đơn nào thiếu điểm. Đơn đã có điểm được giữ nguyên, không tính lại theo quy tắc mới.'}
      </p>
      <Table rowKey="order_id" size="small" loading={loading} columns={columns} dataSource={rows} pagination={false} scroll={{ y: 320 }} />
    </Modal>
  );
};

export default RecalculatePointsModal;
